import { apiInternals, send } from './client';

/**
 * The conduct-complaint workflow (M-11, Task 4) — `GET/POST /justifications`
 * and the `answer`/`decide` transition routes. Every shape here is verified
 * field-for-field against `App\Http\Resources\Justifications\
 * JustificationResource` and `App\Support\OpenApi\ApiSpecification`'s own
 * `Justification` schema in the API repository, not inferred from the task
 * brief's prose — `escalations.ts`'s own docblock states the same
 * discipline for its module, and this one repeats it.
 *
 * ## `list` is built on `request`, never `send` (`escalations.ts`'s own rule)
 *
 * `GET /justifications` is cursor-paginated exactly like `GET /escalations` —
 * `{ data, meta: { next_cursor, per_page, total } }`. `send()` resolves to
 * `request()`'s `.data` alone and throws `meta` away.
 *
 * ## `mine`/`raised_by_me` serialise as `1`/`0`, never `true`/`false` (spec §2)
 *
 * Laravel's `boolean` validation rule accepts `true`/`false`/`1`/`0`/`'1'`/
 * `'0'` — the STRING `'true'` (`String(true)`) is rejected. `queryString()`
 * below carries the identical fix `escalations.ts`/`requests.ts` document at
 * length. `raised_by_me` is a real server-side filter (the raised-by-me
 * fix) — it once had NO wire equivalent at all; see `JustificationList.tsx`'s
 * own docblock for why that client-side narrowing was a defect on a
 * cursor-paginated list.
 *
 * ## Actions come from the server, never re-derived here
 *
 * Nothing in this module re-implements `JustificationPolicy::answer()`'s
 * "must be the row's own `subject_id`" rule, or `JustificationTransition`'s
 * own state rules (answerable only from `open`/`rejected`, decidable only
 * from `answered`, a rejection requiring `decision_note`). A caller renders
 * what the record currently says and lets `answer`/`decide` below answer
 * with whatever the server decides, including a `403`/`409`/`422` this
 * module does not anticipate.
 *
 * ## `can` — what THIS CALLER may do to THIS row (C-1 fix)
 *
 * `JustificationResource` publishes `can.answer`/`can.decide`, computed
 * server-side from `JustificationPolicy::answer()`/`::decide()` for the
 * authenticated caller against the row — never re-derived here from
 * `permissions` + `status`. An earlier revision of `JustificationDetail.tsx`
 * did exactly that and showed "Answer" to every `justification.decide`
 * holder browsing the queue (an administrator or supervisor), which then
 * always 403'd, because holding the permission is not the same as being
 * the row's own `subject_id`. `JustificationDetail.tsx` reads `can.answer`/
 * `can.decide` directly.
 */

/** The four states `JustificationResource.status` can carry (spec J-4). */
export type JustificationStatus = 'open' | 'answered' | 'approved' | 'rejected';

/** What `GET /justifications` filters `status` on — the four states plus `all` (`JustificationIndexRequest::rules()`'s own `Rule::in`). */
export type JustificationStatusFilter = JustificationStatus | 'all';

/** The three values the legacy `<select>` ever emitted (spec J-1). */
export type JustificationCategory = 'missed_call' | 'late_logs' | 'other';

/** `JustificationResource`'s `subject`/`raiser`/`decider` key — `uuid` + `full_name`, always eager-loaded server-side, never a second query from here. */
export interface JustificationPersonRef {
  uuid: string;
  full_name: string;
}

/**
 * What THIS CALLER may do to THIS row — `JustificationResource`'s `can`
 * key (C-1 fix). `answer` is exactly `JustificationPolicy::answer()`
 * (holds `justification.answer` AND is this row's own `subject`). `decide`
 * is `JustificationPolicy::decide()`'s permission check narrowed by the
 * state `JustificationTransition::decide()` itself requires (`status ===
 * 'answered'`, J-7). Neither is re-derivable from `permissions` + `status`
 * alone — this module's own docblock states why.
 */
export interface JustificationAbilities {
  answer: boolean;
  decide: boolean;
}

/**
 * One justification, exactly as `GET /justifications`, `GET /justifications/
 * {justification}`, `POST /justifications` and both transition routes
 * render it — the SAME shape everywhere (`JustificationResource`'s own
 * class docblock).
 */
export interface JustificationRow {
  uuid: string;
  subject: JustificationPersonRef;
  raiser: JustificationPersonRef;
  category: JustificationCategory;
  complaint: string;
  occurred_at: string | null;
  justification: string | null;
  answered_at: string | null;
  status: JustificationStatus;
  decision_note: string | null;
  decider: JustificationPersonRef | null;
  decided_at: string | null;
  created_at: string | null;
  can: JustificationAbilities;
}

/** Every filter `GET /justifications` accepts (`JustificationIndexRequest::rules()`). All optional; `status` defaults to `all` server-side (spec §2). */
export interface JustificationQuery {
  status?: JustificationStatusFilter;
  mine?: boolean;
  /** Narrows to complaints the caller themselves FILED (`raised_by`) — independent of `mine` above, which narrows to complaints ABOUT the caller (`subject_id`). Both present is AND: the two name different columns. */
  raised_by_me?: boolean;
  subject?: string;
  category?: JustificationCategory;
  cursor?: string;
  per_page?: number;
}

/** One page of the queue — cursor-paginated, exactly `EscalationPage`'s shape. */
export interface JustificationPage {
  data: readonly JustificationRow[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    /** How many justifications match the selected filters, not how many are on this page. */
    total: number;
  };
}

/** `POST /justifications`'s body (`StoreJustificationRequest`) — all four required. */
export interface StoreJustificationInput {
  subject_uuid: string;
  category: JustificationCategory;
  complaint: string;
  occurred_at: string;
}

/** `POST .../answer`'s body (`AnswerJustificationRequest`) — required, unlike an escalation's optional `notes` (spec J-3, J-5). */
export interface AnswerJustificationInput {
  justification: string;
}

/** `POST .../decide`'s body (`DecideJustificationRequest`). `decision_note` is required only when rejecting (spec J-7); omitting it entirely leaves the stored note untouched. */
export interface DecideJustificationInput {
  decision: 'approve' | 'reject';
  decision_note?: string | null;
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper `escalations.ts`/`requests.ts`/`cases.ts` each
 * redeclare locally for the same reason (none is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | boolean | undefined][]) {
    if (value === undefined) continue;

    // A BOOLEAN GOES OVER THE WIRE AS `1`/`0`, NEVER `true`/`false` — see this
    // module's own docblock for why `mine=true` would 422.
    search.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

const { request } = apiInternals;

export const justificationsApi = {
  /**
   * DELIBERATELY NOT `send<JustificationPage>()` — see this module's own
   * docblock for why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: JustificationQuery = {}): Promise<JustificationPage> => {
    const { data, meta } = await request<readonly JustificationRow[]>(`/justifications${queryString(query)}`);
    return { data, meta: meta as JustificationPage['meta'] };
  },

  get: (uuid: string) => send<JustificationRow>(`/justifications/${uuid}`),

  /** `422` naming `subject_uuid` when the caller names themselves (spec §2). */
  create: (input: StoreJustificationInput) =>
    send<JustificationRow>('/justifications', { method: 'POST', body: input }),

  /** `403` when the caller is not the complaint's own subject (spec J-3). `409` from `answered`/`approved` (spec J-5). Never predicted client-side; see this module's own docblock. */
  answer: (uuid: string, input: AnswerJustificationInput) =>
    send<JustificationRow>(`/justifications/${uuid}/answer`, { method: 'POST', body: input }),

  /** `409` unless the row is currently `answered` (spec J-4). `422` naming `decision_note` when rejecting with none (spec J-7). */
  decide: (uuid: string, input: DecideJustificationInput) =>
    send<JustificationRow>(`/justifications/${uuid}/decide`, { method: 'POST', body: input }),
};
